import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  useAcquisitionSummaryQuery,
  useRegisterTagsFromGraphMutation,
  type RegisterTagsFromGraphResult,
  type RegisterTagsTransport,
  type TagContainerScope
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'
import {
  DEFAULT_CADENCE_MS,
  buildCadenceOptions,
  formatCadence
} from '../SettingsPage/connection-health/cadence'
import {
  acquisitionDestinationOptions,
  isHttpGateway,
  isOpcEndpoint,
  normalizeAcquisitionUrl
} from './acquisition-source'

export interface RegisterTagsSelection {
  nodeIds: string[]
  containerScope?: TagContainerScope
  searchQuery?: string
}

interface RegisterTagsDialogProps {
  open: boolean
  selection: RegisterTagsSelection
  onOpenChange: (open: boolean) => void
  onRegistered?: (result: RegisterTagsFromGraphResult) => void
}

function resultLabel(value: string): string {
  if (!value) return 'Ready'
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export default function RegisterTagsDialog({
  open,
  selection,
  onOpenChange,
  onRegistered
}: RegisterTagsDialogProps) {
  const summaryQuery = useAcquisitionSummaryQuery()
  const [registerTags, registerState] = useRegisterTagsFromGraphMutation()
  const [transport, setTransport] = useState<RegisterTagsTransport>('webdev')
  const [gatewayUrl, setGatewayUrl] = useState('')
  const [opcEndpointUrl, setOpcEndpointUrl] = useState('')
  const [frequencyMs, setFrequencyMs] = useState(DEFAULT_CADENCE_MS)
  const [preview, setPreview] = useState<RegisterTagsFromGraphResult | null>(null)
  const [result, setResult] = useState<RegisterTagsFromGraphResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const hasSelector =
    selection.nodeIds.length > 0 ||
    selection.containerScope !== undefined ||
    Boolean(selection.searchQuery?.trim())
  const selectionKey = JSON.stringify({
    nodeIds: selection.nodeIds,
    containerScope: selection.containerScope,
    searchQuery: selection.searchQuery
  })

  const gateways = useMemo(
    () => acquisitionDestinationOptions(summaryQuery.data?.sourceUrls, 'poll'),
    [summaryQuery.data?.sourceUrls]
  )
  const opcEndpoints = useMemo(
    () => acquisitionDestinationOptions(summaryQuery.data?.sourceUrls, 'subscribe'),
    [summaryQuery.data?.sourceUrls]
  )
  const cadenceOptions = useMemo(() => buildCadenceOptions(frequencyMs), [frequencyMs])

  useEffect(() => {
    if (!open) return
    setTransport('webdev')
    setGatewayUrl('')
    setOpcEndpointUrl('')
    setFrequencyMs(DEFAULT_CADENCE_MS)
    setPreview(null)
    setResult(null)
    setError(null)
  }, [open, selectionKey])

  useEffect(() => {
    if (!open || transport !== 'webdev') return
    if (gateways.length === 1) {
      setGatewayUrl((current) => current || gateways[0])
    }
  }, [gateways, open, selectionKey, transport])

  useEffect(() => {
    if (!open || transport !== 'opc') return
    if (opcEndpoints.length === 1) {
      setOpcEndpointUrl((current) => current || opcEndpoints[0])
    }
  }, [open, opcEndpoints, selectionKey, transport])

  const resetReview = () => {
    setPreview(null)
    setResult(null)
    setError(null)
  }

  const request = async (previewOnly: boolean) => {
    const normalizedGatewayUrl = normalizeAcquisitionUrl(gatewayUrl)
    const normalizedOpcEndpointUrl = normalizeAcquisitionUrl(opcEndpointUrl)
    if (transport === 'webdev') {
      if (!normalizedGatewayUrl) {
        setError('Ignition HTTP gateway is required for WebDev polling.')
        return
      }
      if (!isHttpGateway(normalizedGatewayUrl)) {
        setError('Ignition HTTP gateway must be an absolute HTTP or HTTPS URL.')
        return
      }
    } else {
      if (!normalizedOpcEndpointUrl) {
        setError('OPC endpoint is required for OPC subscribe.')
        return
      }
      if (!isOpcEndpoint(normalizedOpcEndpointUrl)) {
        setError('OPC endpoint must be an absolute opc.tcp URL.')
        return
      }
    }
    setError(null)
    try {
      const response = await registerTags({
        nodeIds: previewOnly ? selection.nodeIds : (preview?.resolvedNodeIds ?? []),
        containerScope: previewOnly ? selection.containerScope : undefined,
        searchQuery: previewOnly ? selection.searchQuery : undefined,
        preview: previewOnly,
        transport,
        gatewayUrl: transport === 'webdev' ? normalizedGatewayUrl : undefined,
        opcEndpointUrl: transport === 'opc' ? normalizedOpcEndpointUrl : undefined,
        frequencyMs,
        enabled: true
      })
      if (previewOnly) {
        setPreview(response)
        setResult(null)
      } else {
        setResult(response)
        onRegistered?.(response)
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    }
  }

  const reviewed = result ?? preview
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-hidden p-0 sm:max-w-155">
        <DialogHeader className="border-b border-line px-5 py-4">
          <DialogTitle className="text-base">Register history</DialogTitle>
          <DialogDescription className="text-12">
            {selection.nodeIds.length > 0
              ? `Preview ${selection.nodeIds.length.toLocaleString()} selected graph tag${
                  selection.nodeIds.length === 1 ? '' : 's'
                } before creating history registrations.`
              : 'Preview the selected graph scope before creating history registrations.'}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 space-y-3 overflow-y-auto px-5 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="register-tags-transport" className="text-11">
                Transport
              </Label>
              <Select
                value={transport}
                onValueChange={(value) => {
                  if (value === 'webdev' || value === 'opc') {
                    setTransport(value)
                    resetReview()
                  }
                }}
                disabled={registerState.isLoading}
              >
                <SelectTrigger
                  id="register-tags-transport"
                  size="sm"
                  aria-label="History transport"
                  data-testid="register-tags-transport"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="webdev">WebDev poll</SelectItem>
                  <SelectItem value="opc">OPC subscribe</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="register-tags-cadence" className="text-11">
                Cadence
              </Label>
              <Select
                value={String(frequencyMs)}
                onValueChange={(value) => {
                  setFrequencyMs(Number(value))
                  resetReview()
                }}
                disabled={registerState.isLoading}
              >
                <SelectTrigger
                  id="register-tags-cadence"
                  size="sm"
                  aria-label="History cadence"
                  data-testid="register-tags-cadence"
                >
                  <SelectValue>{formatCadence(frequencyMs)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {cadenceOptions.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {transport === 'webdev' ? (
            <div className="space-y-1.5">
              <Label htmlFor="register-tags-gateway" className="text-11">
                Ignition HTTP gateway
              </Label>
              <Input
                id="register-tags-gateway"
                type="url"
                value={gatewayUrl}
                onChange={(event) => {
                  setGatewayUrl(event.target.value)
                  resetReview()
                }}
                placeholder="https://gateway.example.com:8088"
                className="h-8 font-mono text-11"
                disabled={registerState.isLoading}
                data-testid="register-tags-gateway"
              />
              {gateways.length > 0 ? (
                <div
                  className="flex flex-wrap items-center gap-1.5"
                  data-testid="gateway-suggestions"
                >
                  <span className="text-10 text-ink-3">Known gateways</span>
                  {gateways.map((gateway) => (
                    <Button
                      key={gateway}
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 max-w-full truncate px-2 font-mono text-10"
                      title={gateway}
                      onClick={() => {
                        setGatewayUrl(gateway)
                        resetReview()
                      }}
                      disabled={registerState.isLoading}
                    >
                      {gateway}
                    </Button>
                  ))}
                </div>
              ) : null}
              {summaryQuery.error ? (
                <p className="m-0 text-11 text-st-down">
                  Gateways unavailable: {summaryQuery.error}
                </p>
              ) : !summaryQuery.isLoading && gateways.length === 0 ? (
                <p className="m-0 text-11 text-ink-3">
                  Enter the Ignition HTTP gateway used for WebDev polling.
                </p>
              ) : gateways.length > 1 ? (
                <p className="m-0 text-11 text-ink-3">
                  Choose a known gateway or enter another HTTP URL.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="register-tags-opc-endpoint" className="text-11">
                OPC endpoint
              </Label>
              <Input
                id="register-tags-opc-endpoint"
                value={opcEndpointUrl}
                onChange={(event) => {
                  setOpcEndpointUrl(event.target.value)
                  resetReview()
                }}
                placeholder="opc.tcp://gateway.example.com:62541"
                className="h-8 font-mono text-11"
                disabled={registerState.isLoading}
                data-testid="register-tags-opc-endpoint"
              />
              {opcEndpoints.length > 0 ? (
                <div
                  className="flex flex-wrap items-center gap-1.5"
                  data-testid="opc-endpoint-suggestions"
                >
                  <span className="text-10 text-ink-3">Known endpoints</span>
                  {opcEndpoints.map((endpoint) => (
                    <Button
                      key={endpoint}
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 max-w-full truncate px-2 font-mono text-10"
                      title={endpoint}
                      onClick={() => {
                        setOpcEndpointUrl(endpoint)
                        resetReview()
                      }}
                      disabled={registerState.isLoading}
                    >
                      {endpoint}
                    </Button>
                  ))}
                </div>
              ) : null}
              {summaryQuery.error ? (
                <p className="m-0 text-11 text-st-down">
                  Endpoints unavailable: {summaryQuery.error}
                </p>
              ) : !summaryQuery.isLoading && opcEndpoints.length === 0 ? (
                <p className="m-0 text-11 text-ink-3">
                  Enter the concrete opc.tcp endpoint that will collect these tags.
                </p>
              ) : opcEndpoints.length > 1 ? (
                <p className="m-0 text-11 text-ink-3">
                  Choose a known endpoint or enter another opc.tcp URL.
                </p>
              ) : null}
            </div>
          )}

          {reviewed ? (
            <section
              className="rounded-md border border-line bg-bg-sunk/40 p-3"
              data-testid={result ? 'register-tags-result' : 'register-tags-preview'}
            >
              <div className="grid grid-cols-2 gap-2 text-12">
                {result ? (
                  <>
                    <div>
                      <span className="text-ink-3">Created</span>{' '}
                      <strong>{result.createdCount.toLocaleString()}</strong>
                    </div>
                    <div>
                      <span className="text-ink-3">Updated</span>{' '}
                      <strong>{result.updatedCount.toLocaleString()}</strong>
                    </div>
                    <div>
                      <span className="text-ink-3">Quarantined</span>{' '}
                      <strong>{result.quarantinedCount.toLocaleString()}</strong>
                    </div>
                    <div>
                      <span className="text-ink-3">Failed</span>{' '}
                      <strong>{result.failedCount.toLocaleString()}</strong>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <span className="text-ink-3">Ready</span>{' '}
                      <strong>{preview?.registrableCount.toLocaleString()}</strong>
                    </div>
                    <div>
                      <span className="text-ink-3">Excluded</span>{' '}
                      <strong>{preview?.excludedCount.toLocaleString()}</strong>
                    </div>
                  </>
                )}
              </div>

              {reviewed.items.length > 0 ? (
                <div className="mt-2 max-h-44 overflow-auto border-t border-line pt-1">
                  {reviewed.items.map((item, index) => {
                    const state = item.outcome || item.status
                    return (
                      <div
                        key={`${item.nodeId}-${index}`}
                        className="flex items-start justify-between gap-3 border-b border-dashed border-line py-1.5 last:border-0"
                      >
                        <div className="min-w-0">
                          <div className="truncate font-mono text-11 text-ink">
                            {item.tagPath || item.nodeId}
                          </div>
                          {item.reason ? (
                            <div className="text-10 leading-snug text-ink-3">{item.reason}</div>
                          ) : null}
                        </div>
                        <span className="shrink-0 rounded bg-bg px-1.5 py-0.5 text-10 font-semibold text-ink-2">
                          {resultLabel(state)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </section>
          ) : null}

          {error ? (
            <div className="rounded-md bg-st-down-bg p-2 text-12 text-st-down">{error}</div>
          ) : null}
        </div>

        <DialogFooter className="border-t border-line px-5 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={registerState.isLoading}
          >
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result ? (
            preview ? (
              <Button
                size="sm"
                onClick={() => void request(false)}
                disabled={
                  registerState.isLoading ||
                  preview.registrableCount === 0 ||
                  preview.resolvedNodeIds.length === 0
                }
                data-testid="register-tags-apply"
              >
                {registerState.isLoading ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                    Registering…
                  </>
                ) : (
                  `Register ${preview.registrableCount.toLocaleString()} tag${
                    preview.registrableCount === 1 ? '' : 's'
                  }`
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => void request(true)}
                disabled={
                  registerState.isLoading ||
                  !hasSelector ||
                  (transport === 'webdev' ? !gatewayUrl.trim() : !opcEndpointUrl.trim())
                }
                data-testid="register-tags-preview-action"
              >
                {registerState.isLoading ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                    Previewing…
                  </>
                ) : (
                  'Preview registration'
                )}
              </Button>
            )
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
